import type { GuideAnchor } from '../types';

/**
 * Anchor string → live element.
 *
 * Audit §4: the daily-log form has 86 controls and, before Phase 0, zero ids,
 * zero name attributes and zero label[for] pairs. Phase 0 added
 * data-guide-field / data-guide-section / data-guide-action; table CELLS were
 * already addressable by aria-label ("Cost Code, row 3"), and those survive
 * both the desktop layout and the mobile stacked-card layout, which is the only
 * reason cell anchors are safe to use at all.
 */

const cssEscape = (v: string): string =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&');

export function resolveAnchor(anchor: GuideAnchor, root: ParentNode = document): Element | null {
  const sep = anchor.indexOf(':');
  if (sep === -1) return null;
  const kind = anchor.slice(0, sep);
  const value = anchor.slice(sep + 1).trim();
  if (!value) return null;

  switch (kind) {
    case 'field':
      return root.querySelector(`[data-guide-field="${cssEscape(value)}"]`);
    case 'section':
      return root.querySelector(`[data-guide-section="${cssEscape(value)}"]`);
    case 'action':
      return root.querySelector(`[data-guide-action="${cssEscape(value)}"]`);
    case 'cell':
    case 'panel':
      return root.querySelector(`[aria-label="${cssEscape(value)}"]`);
    default:
      return null;
  }
}

/** True when the anchor names something currently on the page. */
export function anchorExists(anchor: GuideAnchor, root: ParentNode = document): boolean {
  return resolveAnchor(anchor, root) !== null;
}

/**
 * The rectangle a highlight may safely land in: the viewport minus the Guide
 * panel and minus the portal's bottom navigation.
 *
 * This is the crux of the occlusion rule (plan, "Panel placement and
 * occlusion"). FieldFlo's widget — the reference Michael supplied — covers the
 * form it is helping with, which makes "show me where" impossible. Scrolling to
 * the raw viewport centre would reproduce exactly that bug.
 */
export interface SafeArea {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export function safeArea(
  viewport: { width: number; height: number },
  panelRect: DOMRect | null,
  bottomChromeHeight: number,
  topChromeHeight = 0,
): SafeArea {
  const area: SafeArea = {
    // The portal's fixed navbar is 80px of chrome the app does not own; a
    // target scrolled to y=0 sits behind it.
    top: topChromeHeight,
    bottom: viewport.height - bottomChromeHeight,
    left: 0,
    right: viewport.width,
  };
  if (!panelRect || panelRect.width === 0 || panelRect.height === 0) return area;

  // Which edge is the panel on? Only ever one — it is docked, never floating
  // in the middle — so shrink that side rather than trying to model a hole.
  //
  // WIDTH is the discriminator, not height: a 70vh bottom sheet is most of the
  // viewport's height, so a height test reads it as a side dock and trims the
  // wrong axis. A sheet spans the width; a side dock never does.
  const spansFullWidth = panelRect.width >= viewport.width * 0.9;

  if (spansFullWidth) {
    // Bottom sheets are inset by the app's bottom nav, so "hugs the bottom"
    // has to be measured against the chrome, not the raw viewport.
    const bottomEdge = viewport.height - bottomChromeHeight;
    if (panelRect.bottom >= bottomEdge - 2) area.bottom = Math.min(area.bottom, panelRect.top);
    else area.top = Math.max(area.top, panelRect.bottom);
    return area;
  }

  const hugsRight = viewport.width - panelRect.right <= panelRect.left;
  if (hugsRight) area.right = Math.min(area.right, panelRect.left);
  else area.left = Math.max(area.left, panelRect.right);
  return area;
}

/** The nearest ancestor that actually scrolls — on mobile that is <main>, not the window. */
export function scrollParentOf(el: Element): Element | Window {
  let node: Element | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    const scrolls = /(auto|scroll|overlay)/.test(style.overflowY);
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return window;
}

/**
 * Bring `el` into the safe area. scrollIntoView handles arbitrary nesting for
 * us; the correction afterwards is what keeps the target clear of the panel,
 * which scrollIntoView knows nothing about.
 */
export function scrollIntoSafeArea(el: Element, area: SafeArea): void {
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

  const rect = el.getBoundingClientRect();
  const overshootBottom = rect.bottom - area.bottom;
  const overshootTop = area.top - rect.top;
  const delta = overshootBottom > 0 ? overshootBottom + 16 : overshootTop > 0 ? -(overshootTop + 16) : 0;
  if (delta === 0) return;

  const parent = scrollParentOf(el);
  if (parent === window) window.scrollBy({ top: delta, behavior: 'smooth' });
  else (parent as Element).scrollBy({ top: delta, behavior: 'smooth' });
}
